import React, { createContext, useEffect, useState, useContext } from 'react';

import { AuthContext } from './AuthContext';
import { decodeJwtUnsafe } from './govbr';
import { User } from '../../types/User';
import { UserVinculoToken } from '../../types/UsuarioVinculo';

export interface UserContextType {
  user: User | null;
  loading: boolean;
  cudProdutoDaf: boolean;
  cudSes: boolean;
  cudFornecedor: boolean;
}

export const UserContext = createContext<UserContextType>({
  user: null,
  loading: true,
  cudProdutoDaf: false,
  cudSes: false,
  cudFornecedor: false,
});

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const { user: authUser } = useContext(AuthContext)!;
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const determinarPapel = (payload: any): string => {
    const relacoesInterface =
      payload?.relacoesInterface || payload?.relacoes_interface;

    const toNum = (v: any): number | null => {
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };

    if (Array.isArray(relacoesInterface) && relacoesInterface.length > 0) {
      const rels: number[] = relacoesInterface
        .map(toNum)
        .filter((n): n is number => n !== null);

      if (rels.includes(3)) return 'ses';
      if (rels.includes(2)) return 'fornecedor';
      if (rels.includes(1)) return 'daf';

      switch (rels[0]) {
        case 3:
          return 'ses';
        case 2:
          return 'fornecedor';
        case 1:
          return 'daf';
        default:
          break;
      }
    }

    if (
      payload?.cnpjs &&
      Array.isArray(payload.cnpjs) &&
      payload.cnpjs.length > 0
    ) {
      return 'fornecedor';
    }

    const statusInterface =
      payload?.statusInterface || payload?.status_interface;
    if (payload?.cnpj && statusInterface) {
      return 'fornecedor';
    }

    const tpVinculo = payload?.tpVinculo ?? payload?.tp_vinculo;
    if (tpVinculo !== undefined && tpVinculo !== null) {
      switch (String(tpVinculo)) {
        case '2':
          return 'ses';
        case '1':
          return 'fornecedor';
        case '0':
          return 'daf';
      }
    }

    if (payload?.unidade !== undefined && payload?.unidade !== null) {
      if (payload.unidade > 1000) return 'ses';
      if (payload.unidade > 0) return 'daf';
      if (payload.unidade === 0) return 'daf';
    }

    return 'daf';
  };

  const buildUserFromPayload = (payload: any): User => {
    const subNumber =
      typeof payload?.sub === 'number'
        ? payload.sub
        : Number(payload?.sub ?? 0);
    const papel = determinarPapel(payload);
    return {
      sub: subNumber,
      nome: String(payload?.name ?? payload?.nome ?? ''),
      email: String(payload?.email ?? ''),
      papel,
      permissoes: payload?.permissoes || [],
      relacoesInterface: payload?.relacoesInterface || [],
      cnpjs: payload?.cnpjs || [],
    };
  };

  const getRedirectPath = (papel: string): string => {
    if (window.location.hash) {
      return window.location.hash;
    }
    let redirectTo: string;
    switch (papel.toLowerCase()) {
      case 'fornecedor':
        redirectTo = '/#/fornecedor/meusdados/usuario';
        break;
      case 'ses':
        redirectTo = '/#/secsaude/medsus/gestao';
        break;
      case 'daf':
      default:
        redirectTo = '/#/daf/home/inicio';
        break;
    }
    return redirectTo;
  };

  const calcularPodeCudProdutoDaf = (payload: any): boolean => {
    const vinculos: UserVinculoToken[] = Array.isArray(payload?.vinculo)
      ? payload.vinculo
      : [];

    return vinculos.some(
      (v: UserVinculoToken) =>
        v.superAdmin === true ||
        (v.interface === 1 && v.idInstituicao === 1 && v.status === 'Ativo'),
    );
  };

  const [cudProdutoDaf, setcudProdutoDaf] = useState(false);
  const [cudSes, setcudSes] = useState(false);
  const [cudFornecedor, setcudFornecedor] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');

    if (!token) {
      setUser(null);
      setcudProdutoDaf(false);
      setLoading(false);
      return;
    }

    try {
      const payload = decodeJwtUnsafe(token);
      if (!payload) {
        setUser(null);
        setcudProdutoDaf(false);
        setLoading(false);
        return;
      }

      const userData = buildUserFromPayload(payload);
      setUser(userData);

      // ✅ permissão CUD
      const cud = calcularPodeCudProdutoDaf(payload);
      // console.log('CUD calculado:', cud);

      setcudProdutoDaf(cud);
      setcudSes(cud);
      setcudFornecedor(cud);

      // ✅ redirecionamento inicial
      if (
        window.location.hash === '#/' ||
        window.location.hash === '' ||
        window.location.pathname === '/'
      ) {
        const redirectTo = getRedirectPath(payload);
        // console.log('Redirecionando para:', redirectTo);
        window.location.replace(redirectTo);
      }
    } catch (error) {
      console.error('Erro ao processar usuário:', error);
      setUser(null);
      setcudProdutoDaf(false);
      setcudSes(false);
      setcudFornecedor(false);
      setLoading(false);
    }
  }, [authUser]);

  return (
    <UserContext.Provider
      value={{ user, loading, cudProdutoDaf, cudSes, cudFornecedor }}
    >
      {children}
    </UserContext.Provider>
  );
};
